import React, { useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';

import { DeviceContext, deviceType, CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import DesktopEmailVerifyResultPage from './DesktopEmailVerifyResultPage';
import MobileEmailVerifyResultPage from './MobileEmailVerifyResultPage';

import { SEND_VERIFY_EMAIL } from '@src/pages/EmailVerifyRequest/queries';
import { sendVerifyEmail } from '@src/pages/EmailVerifyRequest/__generated__/sendVerifyEmail';
import { VERIFY_EMAIL } from '@src/pages/EmailVerifyResult/queries';
import { verifyEmail } from '@src/pages/EmailVerifyResult/__generated__/verifyEmail';
import { tokenCreate_tokenCreate } from '@src/providers/CredentialProvider/__generated__/tokenCreate';

import { getGraphqlIdFromDBId } from '@utils';
import { padBase64 } from '@src/utils/deepLink';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';
import { baseUrl } from '@src/app/routes';

interface RouteParams {
  uid: string;
  token: string;
}

const EmailVerifyResultPage = () => {
  const history = useHistory();
  const { uid, token } = useParams<RouteParams>();
  const { isMobile } = useContext<deviceType>(DeviceContext);
  const { currentUser, setCurrentUser } = useContext<credentialType>(CredentialContext);
  const { showSnackbar } = useSnackbar();

  const [verifyEmail, { data: verifyEmailData, loading: verifyEmailLoading }] = useMutation<verifyEmail>(VERIFY_EMAIL, {
    onCompleted: (data) => {
      const errors = data?.verifyEmail?.errors;

      if (errors?.length) {
        showSnackbar({ message: errors[0].message });
      } else {
        const copyCurrentUser: tokenCreate_tokenCreate = JSON.parse(JSON.stringify(currentUser));
        copyCurrentUser.user.emailVerified = true;

        setCurrentUser(copyCurrentUser);
      }
    },
  });

  const [sendVerifyEmail] = useMutation<sendVerifyEmail>(SEND_VERIFY_EMAIL, {
    onCompleted: (data) => {
      const errors = data?.sendVerifyEmail?.errors;

      if (errors?.length) {
        showSnackbar({ message: errors[0].message });
      } else {
        showSnackbar({ message: 'Email has been sent' });
      }
    },
  });

  useEffect(() => {
    if (!currentUser?.token) {
      redirectToLoginWithCurrentPath(history);
    } else {
      const userDbId = atob(padBase64(uid));
      const id = getGraphqlIdFromDBId(userDbId, 'User');

      verifyEmail({
        variables: {
          id,
          input: {
            token,
          },
        },
      });
    }
  }, []);

  const handleClickVerifyLaterButton = () => {
    history.replace(baseUrl);
  };

  const handleClickResendEmailButton = () => {
    sendVerifyEmail();
  };

  return isMobile ? (
    <MobileEmailVerifyResultPage
      loading={verifyEmailLoading}
      isVerified={verifyEmailData?.verifyEmail?.errors?.length ? false : true}
      onClickVerifyLaterButton={handleClickVerifyLaterButton}
      onClickResendEmailButton={handleClickResendEmailButton}
    />
  ) : (
    <DesktopEmailVerifyResultPage
      loading={verifyEmailLoading}
      isVerified={verifyEmailData?.verifyEmail?.errors?.length ? false : true}
      onClickVerifyLaterButton={handleClickVerifyLaterButton}
      onClickResendEmailButton={handleClickResendEmailButton}
    />
  );
};

export default EmailVerifyResultPage;
